import type { ApiRequestBody, ApiResponseBody, ApiSearchQuery } from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetDevDocumentListQuery = ApiSearchQuery<'/v2/pn-force/dev-documents', 'get'>;
export type GetDevDocumentListResponse = ApiResponseBody<'/v2/pn-force/dev-documents', 'get'>;
export type DevDocumentListItem = GetDevDocumentListResponse['items'][0];

/** BE の `orderBy` に載っている列だけがソート可能（UI 側の列 ID とは別軸）。 */
export type DevDocumentOrderBy = NonNullable<GetDevDocumentListQuery>['orderBy'];

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetDevDocumentCountQuery = ApiSearchQuery<'/v2/pn-force/dev-documents/count', 'get'>;
export type GetDevDocumentCountResponse = ApiResponseBody<'/v2/pn-force/dev-documents/count', 'get'>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

/** 詳細は本文の節（`sections`）を `ord` 昇順で含む。`LINK` は常に空配列。 */
export type GetDevDocumentResponse = ApiResponseBody<'/v2/pn-force/dev-documents/{id}', 'get'>;

/**
 * 開発ドキュメントに紐づく関連チケット（詳細レスポンスの `tickets` 要素）。
 * `{ id, code, title, kind, status, priority }`。**read-only**（逆引き表示のみ。
 * 紐付けの書き込みは Ticket 側の PATCH `devDocumentIds` から行う）。
 */
export type DevDocumentTicketItem = GetDevDocumentResponse['tickets'][number];

// ---------------------------------------------------------------------------
// Patch API Types
// ---------------------------------------------------------------------------

/**
 * 共通で更新できるのは `status` / `kind` / `tags` / `summary` / `supersededById`。
 * `title` と本文（`sections`）は `MANAGED` のみ（それ以外は BE が 422）。
 */
export type PatchDevDocumentRequest = ApiRequestBody<'/v2/pn-force/dev-documents/{id}', 'patch'>;

/** PATCH レスポンスは詳細 GET と同 shape。 */
export type PatchDevDocumentResponse = ApiResponseBody<'/v2/pn-force/dev-documents/{id}', 'patch'>;

// ---------------------------------------------------------------------------
// Post Create API Types
// ---------------------------------------------------------------------------

export type PostDevDocumentRequest = ApiRequestBody<'/v2/pn-force/dev-documents', 'post'>;
export type PostDevDocumentResponse = ApiResponseBody<'/v2/pn-force/dev-documents', 'post'>;

// ---------------------------------------------------------------------------
// Attachment API Types
// ---------------------------------------------------------------------------

/** 添付ファイル一覧（`createdAt` 昇順・`id` 昇順 tiebreak、論理削除済みは除く）。 */
export type GetDevDocumentAttachmentsResponse = ApiResponseBody<'/v2/pn-force/dev-documents/{documentId}/attachments', 'get'>;

/** 添付ファイル 1 件（一覧レスポンスの `attachments` 要素）。 */
export type DevDocumentAttachmentItem = GetDevDocumentAttachmentsResponse['attachments'][number];

/** presigned POST 発行リクエスト（この時点では DB 登録しない）。 */
export type CreateDevDocumentAttachmentRequest = ApiRequestBody<'/v2/pn-force/dev-documents/{documentId}/attachments', 'post'>;
export type CreateDevDocumentAttachmentResponse = ApiResponseBody<'/v2/pn-force/dev-documents/{documentId}/attachments', 'post'>;

/** アップロード完了通知（S3 アップロード成功後に呼び、DB 登録する）。 */
export type CompleteDevDocumentAttachmentRequest = ApiRequestBody<'/v2/pn-force/dev-documents/{documentId}/attachments/{attachmentId}/complete', 'post'>;
export type CompleteDevDocumentAttachmentResponse = ApiResponseBody<'/v2/pn-force/dev-documents/{documentId}/attachments/{attachmentId}/complete', 'post'>;
export type GetDevDocumentAttachmentDownloadUrlResponse = ApiResponseBody<'/v2/pn-force/dev-documents/{documentId}/attachments/{attachmentId}/download-url', 'get'>;

// ---------------------------------------------------------------------------
// Dev Document Enums (BE 定義 12 / 3 / 5 / 3 値)
// ---------------------------------------------------------------------------

export const DevDocumentKind = {
  ADR: 'ADR',
  SKILL: 'SKILL',
  REQUIREMENT: 'REQUIREMENT',
  DESIGN: 'DESIGN',
  RULE: 'RULE',
  RUNBOOK: 'RUNBOOK',
  INVESTIGATION: 'INVESTIGATION',
  MEETING_NOTE: 'MEETING_NOTE',
  WORK_LOG: 'WORK_LOG',
  SPREADSHEET: 'SPREADSHEET',
  DIAGRAM: 'DIAGRAM',
  OTHER: 'OTHER'
} as const;
export type DevDocumentKind = (typeof DevDocumentKind)[keyof typeof DevDocumentKind];
const DEV_DOCUMENT_KIND_VALUES: ReadonlySet<string> = new Set(Object.values(DevDocumentKind));
export function isDevDocumentKind(value: string | null | undefined): value is DevDocumentKind {
  return value != null && DEV_DOCUMENT_KIND_VALUES.has(value);
}

/**
 * 正本の所在。UI の編集可否はこの値だけで決まる。
 * - `FILE`: git 上の `.md` が正本。本文と title は編集不可（BE が 422）
 * - `MANAGED`: STACKTRACE が正本。本文と title を編集できる
 * - `LINK`: 外部サービスが正本。本文を持たない
 */
export const DevDocumentOwnership = {
  FILE: 'FILE',
  MANAGED: 'MANAGED',
  LINK: 'LINK'
} as const;
export type DevDocumentOwnership = (typeof DevDocumentOwnership)[keyof typeof DevDocumentOwnership];
export const DevDocumentStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  DEPRECATED: 'DEPRECATED',
  SUPERSEDED: 'SUPERSEDED',
  ARCHIVED: 'ARCHIVED'
} as const;
export type DevDocumentStatus = (typeof DevDocumentStatus)[keyof typeof DevDocumentStatus];
const DEV_DOCUMENT_STATUS_VALUES: ReadonlySet<string> = new Set(Object.values(DevDocumentStatus));
export function isDevDocumentStatus(value: string | null | undefined): value is DevDocumentStatus {
  return value != null && DEV_DOCUMENT_STATUS_VALUES.has(value);
}

/** 一覧画面のステータスタブ用。ドメイン値 + "ALL"。 */
export type DevDocumentStatusFilter = DevDocumentStatus | 'ALL';